import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import dayjs from 'dayjs';
import Api from '../../services/api';
// os componentes:
import {
    LoadingOutlined,
    EditOutlined,
    CopyOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons'
import {
    Row,
    Flex,
    Spin,
    Space,
    Tooltip,
    Typography,
    Table,
    Button,
    Modal,
    Input,
    Divider,
    Tag,
    Checkbox
} from 'antd';

function Templates() {
    const [isFetching, setIsFetching] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [systemsStatus, setSystemsStatus] = useState([]);
    const [targetTemplateToCopy, setTargetTemplateToCopy] = useState(null);
    const [copyTemplateName, setCopyTemplateName] = useState('');
    const [shouldDisableAfterCopy, setShouldDisableAfterCopy] = useState(false);

    /**
     * Busca os modelos de relatórios cadastrados.
     */
    async function fetchTemplates() {
        setIsFetching(true);

        const response = await Api('templates?show_disabled=true').index();

        setTemplates(response.data);
        setIsFetching(false);
    };

    /**
     * Busca a lista de sistemas e a disponibilidade
     * desses serviços.
     */
    async function fetchSystemsStatus() {
        const response = await Api('systems/status', { noPrefix: true }).index();

        setSystemsStatus(response.data);
    }

    useEffect(() => {
        document.title = 'DPEMT - Modelos de Relatórios';

        fetchTemplates();
        fetchSystemsStatus();
    }, []);

    if (isFetching) {
        return (
            <Row justify="center">
                <Flex align="center" gap="middle">
                    <Spin indicator={<LoadingOutlined spin />} size="large">
                        Aguarde enquanto os modelos criados são localizados.
                    </Spin>
                </Flex>
            </Row>
        );
    }

    /**
     * Envia a requisição para gerar a cópia do template.
     * 
     * @param {Number} templateId 
     */
    async function handleCopy(templateId) {
        setIsCopying(true);

        try {
            const response = await Api(`/templates/${templateId}/copy`).store({
                name: `RMA ${copyTemplateName}`,
                shouldDisableAfterCopy
            })

            setShouldDisableAfterCopy(false);
            setCopyTemplateName('');
            setTargetTemplateToCopy(null);
            setTemplates([ response.data, ...templates ])
        } catch {}

        setIsCopying(false);
    }

    const tableColumns = [
        {
            title: 'Nome',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Cópia de',
            key: 'copiaDe',
            render: (_, record) => record.parent ? (
                <Space>
                    <Tooltip title="Duplicar">
                        <Link to={`${process.env.REACT_APP_API_URL}/admin/reports/template/${record.parent.id}/change`}>
                            {record.parent.name}
                        </Link>
                    </Tooltip>
                </Space>
            ) : '-'
        },
        {
            title: 'Nº de Relatórios',
            dataIndex: 'reportCount',
            key: 'reportCount',
        },
        {
            title: 'Criado em',
            dataIndex: 'createdAt',
            key: 'createdAt',
        },
        {
            title: 'Desabilitado em',
            dataIndex: 'disabledAt',
            key: 'disabledAt',
        },
        {
            title: 'Ações',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Gerenciar">
                        <Button
                            type="link"
                            icon={<EditOutlined style={{ fontSize: 14 }} />}
                            onClick={() => {
                                window.open(`${process.env.REACT_APP_API_URL}/admin/reports/template/${record.templateId}/change`, '_blank')
                            }}
                        />
                    </Tooltip>

                    <Tooltip title="Duplicar">
                        <Button
                            type="link"
                            icon={<CopyOutlined style={{ fontSize: 14 }} />}
                            onClick={() => setTargetTemplateToCopy(record)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ]

    const toDataSource = data => data.map(row => ({
        parent: row.template,
        templateId: row.id,
        name: row.name,
        createdAt: dayjs(row.created_at).format('DD/MM/YYYY'),
        disabledAt: row.disabled_at ? dayjs(row.disabled_at).format('DD/MM/YYYY') : '-',
        reportCount: row.report_count
    }))

    return (
        <>
            <Flex justify="space-between">
                <Typography.Title level={2} style={{ margin: 0 }}>
                    Modelos de Relatórios
                </Typography.Title>
            </Flex>

            <Divider style={{ margin: '.5rem 0 .9rem 0' }} />

            <Row style={{ marginBottom: "1rem" }}>
                <Flex gap="4px 0" wrap>
                    <div style={{ marginRight: 8 }}>
                        Disponibilidade dos sistemas externos:
                    </div>

                    {systemsStatus.map(system => (
                        <>
                            {!system.status && (
                                <Link to={system.system_url} target="_blank">
                                    <Tag icon={<CloseCircleOutlined />} color="error">
                                        <b>{system.system_name}</b>: com erro
                                    </Tag>
                                </Link>
                            )}

                            {(system.status >= 200 && system.status <= 299) && (
                                <Link to={system.system_url} target="_blank">
                                    <Tag icon={<CheckCircleOutlined />} color="success">
                                        <b>{system.system_name}</b>: operando
                                    </Tag>
                                </Link>
                            )}
                        </>
                    ))}
                </Flex>
            </Row>

            <Table
                dataSource={toDataSource(templates)}
                columns={tableColumns}
                rowClassName={row => row.disabledAt !== '-' ? 'row--disabled' : ''}
                pagination={false}
            />

            <Modal
                centered
                open={targetTemplateToCopy}
                title={`Copiar ${targetTemplateToCopy?.name}`}
                footer={
                    <Button
                        type="primary"
                        onClick={() => handleCopy(targetTemplateToCopy.templateId)}
                        loading={isCopying}
                        disabled={
                            !copyTemplateName.length ||
                            (
                                (copyTemplateName.toLowerCase().trim() === targetTemplateToCopy.name.replace('RMA ', '').toLowerCase().trim())
                                && !shouldDisableAfterCopy
                            ) ||
                            copyTemplateName.toLowerCase().includes('rma')
                        }
                    >
                        Copiar
                    </Button>
                }
                onCancel={() => {
                    setTargetTemplateToCopy(null);
                    setCopyTemplateName('');
                }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                        allowClear
                        placeholder="Defina um nome para o novo modelo"
                        addonBefore="RMA"
                        onChange={({ target }) => setCopyTemplateName(target.value)}
                        value={copyTemplateName}
                    />

                    <div>
                        <Typography.Text>
                            <small>O prefixo <b>RMA</b> será incluido automaticamente*</small>
                        </Typography.Text>
                    </div>

                    <Space>
                        <Checkbox onChange={({ target }) => setShouldDisableAfterCopy(target.checked)} />

                        Desabilitar o modelo atual ao gerar a cópia.
                    </Space>
                </Space>
            </Modal>
        </>
    );
}

export default Templates;
